import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import cx from 'classnames';
import _difference from 'lodash.difference'; // Reference: https://github.com/Semantic-Org/Semantic-UI-React/blob/master/src/lib/getElementType.js

function detectElementType(Component, props) {
  const defaultProps = Component.defaultProps;

  if (props.as && props.as !== (defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.as)) {
    return props.as;
  }

  if (props.href) {
    return 'a';
  }

  return (defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.as) || 'div';
}

function getUnhandledProps(C, props) {
  // Note that `handledProps` are generated automatically during build with `babel-plugin-transform-react-handled-props`
  return _difference(Object.keys(props), C.handledProps).reduce((acc, prop) => {
    acc[prop] = props[prop];
    return acc;
  }, {});
}

function differenceObject(a, b) {
  const diff = _difference(Object.keys(a), Object.keys(b));

  return diff.reduce((acc, prop) => {
    acc[prop] = a[prop];
    return acc;
  }, {});
}

function properRange(s, bound) {
  let result = s;

  if (typeof s === 'string') {
    result = parseInt(s, 10);
  }

  if (result < bound[0]) {
    result = bound[0];
  }

  if (result > bound[1]) {
    result = bound[1];
  }

  return result;
}

const Column = _ref => {
  let {
    children,
    className,
    ...props
  } = _ref;
  const {
    size,
    offset,
    narrow,
    mobile,
    touch,
    tablet,
    desktop,
    widescreen,
    fullhd
  } = props;
  const rest = getUnhandledProps(Column, props);
  const ElementType = detectElementType(Column, props);
  const classes = cx('column', className, {
    [`is-${properRange(size, [1, 12])}`]: size,
    [`is-offset-${properRange(offset, [1, 12])}`]: offset,
    [`is-narrow${typeof narrow !== 'boolean' ? `-${narrow}` : ''}`]: narrow,
    [`is-${properRange(mobile, [1, 12])}-mobile`]: mobile,
    [`is-${properRange(touch, [1, 12])}-touch`]: touch,
    [`is-${properRange(tablet, [1, 12])}-tablet`]: tablet,
    [`is-${properRange(desktop, [1, 12])}-desktop`]: desktop,
    [`is-${properRange(widescreen, [1, 12])}-widescreen`]: widescreen,
    [`is-${properRange(fullhd, [1, 12])}-mobile`]: fullhd
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Column.handledProps = ["as", "className", "desktop", "fullhd", "mobile", "narrow", "offset", "size", "tablet", "touch", "widescreen"];

const Columns = _ref2 => {
  let {
    children,
    className,
    ...props
  } = _ref2;
  const {
    mobiled,
    desktoped,
    gapless,
    multiline,
    vcentered,
    centered,
    gap,
    mobile,
    touch,
    tablet,
    desktop,
    widescreen,
    fullhd
  } = props;
  const rest = getUnhandledProps(Columns, props);
  const ElementType = detectElementType(Columns, props);
  const classes = cx('columns', className, {
    'is-mobile': mobiled,
    'is-desktop': desktoped,
    'is-gapless': gapless,
    'is-multiline': multiline,
    'is-vcentered': vcentered,
    'is-centered': centered,
    'is-variable': gap || typeof gap === 'number' && gap >= 0 || mobile || touch || tablet || desktop || widescreen || fullhd,
    [`is-${properRange(gap, [0, 8])}`]: gap || typeof gap === 'number' && gap >= 0,
    [`is-${properRange(mobile, [0, 8])}-mobile`]: mobile,
    [`is-${properRange(touch, [0, 8])}-touch`]: touch,
    [`is-${properRange(tablet, [0, 8])}-tablet`]: tablet,
    [`is-${properRange(desktop, [0, 8])}-desktop`]: desktop,
    [`is-${properRange(widescreen, [0, 8])}-widescreen`]: widescreen,
    [`is-${properRange(fullhd, [0, 8])}-mobile`]: fullhd
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Columns.handledProps = ["as", "centered", "className", "desktop", "desktoped", "fullhd", "gap", "gapless", "mobile", "mobiled", "multiline", "tablet", "touch", "vcentered", "widescreen"];

const Block = _ref3 => {
  let {
    children,
    className,
    ...props
  } = _ref3;
  const rest = getUnhandledProps(Block, props);
  const ElementType = detectElementType(Block, props);
  const classes = cx('block', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Block.handledProps = ["as", "className"];

const Box = _ref4 => {
  let {
    children,
    className,
    ...props
  } = _ref4;
  const rest = getUnhandledProps(Box, props);
  const ElementType = detectElementType(Box, props);
  const classes = cx('box', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Box.handledProps = ["as", "className"];

const Button = _ref5 => {
  let {
    children,
    className,
    ...props
  } = _ref5;
  const rest = getUnhandledProps(Button, props);
  const ElementType = detectElementType(Button, props);
  const classes = cx('button', className, {
    [`is-${props.color}`]: props.color,
    'is-light': props.color === 'light' || props.light,
    [`is-${props.size}`]: props.size,
    'is-fullwidth': props.fullwidth,
    'is-outlined': props.outlined,
    'is-inverted': props.inverted,
    'is-rounded': props.rounded,
    'is-hovered': props.hovered,
    'is-focused': props.focused,
    'is-active': props.active,
    'is-loading': props.loading,
    'is-static': props.static,
    'is-selected': props.selected
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Button.handledProps = ["active", "as", "className", "color", "focused", "fullwidth", "hovered", "inverted", "light", "loading", "outlined", "rounded", "selected", "size", "static"];
Button.defaultProps = {
  as: 'button'
};

const Buttons = _ref6 => {
  let {
    children,
    className,
    ...props
  } = _ref6;
  const ElementType = detectElementType(Buttons, props);
  const classes = cx('buttons', className, {
    [`are-${props.size}`]: props.size,
    'has-addons': props.addons,
    [`is-${props.align}`]: props.align
  });
  return /*#__PURE__*/React.createElement(ElementType, {
    className: classes
  }, children);
};

Buttons.handledProps = ["addons", "align", "as", "className", "size"];

const Content = _ref7 => {
  let {
    children,
    className,
    ...props
  } = _ref7;
  const rest = getUnhandledProps(Content, props);
  const ElementType = detectElementType(Content, props);
  const classes = cx('content', className, {
    [`is-${props.size}`]: props.size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Content.handledProps = ["as", "className", "size"];

const Delete = _ref8 => {
  let {
    className,
    ...props
  } = _ref8;
  const rest = getUnhandledProps(Delete, props);
  const ElementType = detectElementType(Delete, props);
  const classes = cx('delete', className, {
    [`is-${props.size}`]: props.size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }));
};

Delete.handledProps = ["as", "className", "size"];
Delete.defaultProps = {
  as: 'button'
};

const Icon = _ref9 => {
  let {
    className,
    ...props
  } = _ref9;
  const {
    name,
    size,
    alignment,
    panel
  } = props;
  const rest = getUnhandledProps(Icon, props);
  const ElementType = detectElementType(Icon, props);
  const classes = cx(panel ? 'panel-icon' : 'icon', className, {
    [`is-${size}`]: size,
    [`is-${alignment}`]: alignment
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), /*#__PURE__*/React.createElement("i", {
    className: name
  }));
};

Icon.handledProps = ["alignment", "as", "className", "name", "panel", "size"];
Icon.defaultProps = {
  as: 'span'
};

const IconText = _ref10 => {
  let {
    children,
    className,
    ...props
  } = _ref10;
  const rest = getUnhandledProps(IconText, props);
  const ElementType = detectElementType(IconText, props);
  const classes = cx('icon-text', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

IconText.handledProps = ["as", "className"];
IconText.defaultProps = {
  as: 'span'
};

const Image = _ref11 => {
  let {
    children,
    className,
    ...props
  } = _ref11;
  const {
    src,
    alt
  } = props;
  const imgProps = props.imgProps;
  const imgClassName = props.rounded ? `${imgProps !== null && imgProps !== void 0 && imgProps.className ? imgProps.className + ' ' : ''}is-rounded` : imgProps === null || imgProps === void 0 ? void 0 : imgProps.className;
  const rest = getUnhandledProps(Image, props);
  const ElementType = detectElementType(Image, props);
  const classes = cx('image', className, {
    [`is-${props.size}x${props.size}`]: props.size,
    [`is-square`]: props.square,
    [`is-${props.by}`]: props.by
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children || /*#__PURE__*/React.createElement("img", _extends({}, imgProps, {
    className: imgClassName,
    src: src,
    alt: alt
  })));
};

Image.handledProps = ["alt", "as", "by", "className", "imgProps", "rounded", "size", "square", "src"];
Image.defaultProps = {
  as: 'figure'
};

const Notification = _ref12 => {
  let {
    children,
    className,
    ...props
  } = _ref12;
  const rest = getUnhandledProps(Notification, props);
  const ElementType = detectElementType(Notification, props);
  const classes = cx('notification', className, {
    [`is-${props.color}`]: props.color,
    [`is-light`]: props.color === 'light' || props.light
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Notification.handledProps = ["as", "className", "color", "light"];

const Progress = _ref13 => {
  let {
    children,
    className,
    ...props
  } = _ref13;
  const rest = getUnhandledProps(Progress, props);
  const classes = cx('progress', className, {
    [`is-${props.color}`]: props.color,
    [`is-${props.size}`]: props.size
  });
  return /*#__PURE__*/React.createElement("progress", _extends({}, rest, {
    className: classes,
    value: !props.indeterminate && rest.value
  }), children || (rest.value ? `${rest.value}%` : null));
};

Progress.handledProps = ["as", "className", "color", "indeterminate", "size"];

const Table = _ref14 => {
  let {
    children,
    className,
    ...props
  } = _ref14;
  const rest = getUnhandledProps(Table, props);
  const classes = cx('table', className, {
    'is-bordered': props.bordered,
    'is-striped': props.striped,
    'is-narrow': props.narrow,
    'is-hoverable': props.hoverable,
    'is-fullwidth': props.fullwidth
  });
  return /*#__PURE__*/React.createElement("table", _extends({}, rest, {
    className: classes
  }), children);
};

Table.handledProps = ["as", "bordered", "className", "fullwidth", "hoverable", "narrow", "striped"];

const TableContainer = _ref15 => {
  let {
    children,
    className,
    ...props
  } = _ref15;
  const rest = getUnhandledProps(TableContainer, props);
  const ElementType = detectElementType(TableContainer, props);
  const classes = cx('table-container', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

TableContainer.handledProps = ["as", "className"];

const Tag = _ref16 => {
  let {
    children,
    className,
    ...props
  } = _ref16;
  const rest = getUnhandledProps(Tag, props);
  const ElementType = detectElementType(Tag, props);
  const classes = cx('tag', className, {
    [`is-${props.color}`]: props.color,
    [`is-light`]: props.color === 'light' || props.light,
    [`is-${props.size}`]: props.size,
    'is-rounded': props.rounded,
    'is-delete': props.delete
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Tag.handledProps = ["as", "className", "color", "delete", "light", "rounded", "size"];
Tag.defaultProps = {
  as: 'span'
};

const Tags = _ref17 => {
  let {
    children,
    className,
    ...props
  } = _ref17;
  const ElementType = detectElementType(Tags, props);
  const classes = cx('tags', className, {
    [`are-${props.size}`]: props.size,
    'has-addons': props.addons
  });
  return /*#__PURE__*/React.createElement(ElementType, {
    className: classes
  }, children);
};

Tags.handledProps = ["addons", "as", "className", "size"];

const Title = _ref18 => {
  let {
    children,
    className,
    ...props
  } = _ref18;
  const {
    as,
    spaced
  } = props;
  const rest = getUnhandledProps(Title, props);
  const ElementType = detectElementType(Title, props);
  const size = as && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(as) ? properRange(as.substring(1), [1, 6]) : props.size ? properRange(props.size, [1, 6]) : undefined;
  const classes = cx('title', className, {
    [`is-${size}`]: size,
    'is-spaced': spaced
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Title.handledProps = ["as", "className", "size", "spaced"];

const Subtitle = _ref19 => {
  let {
    children,
    className,
    ...props
  } = _ref19;
  const rest = getUnhandledProps(Subtitle, props);
  const ElementType = detectElementType(Subtitle, props);
  const size = props.as && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(props.as) ? properRange(props.as.substring(1), [1, 6]) : props.size ? properRange(props.size, [1, 6]) : undefined;
  const classes = cx('subtitle', className, {
    [`is-${size}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Subtitle.handledProps = ["as", "className", "size"];

const Breadcrumb = _ref20 => {
  let {
    children,
    className,
    ...props
  } = _ref20;
  const {
    alignment,
    separator,
    size
  } = props;
  const rest = getUnhandledProps(Breadcrumb, props);
  const ElementType = detectElementType(Breadcrumb, props);
  const classes = cx('breadcrumb', className, {
    [`is-${alignment}`]: alignment,
    [`has-${separator}-separator`]: separator,
    [`is-${size}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Breadcrumb.handledProps = ["alignment", "as", "className", "separator", "size"];
Breadcrumb.defaultProps = {
  as: 'nav'
};

const Card = _ref21 => {
  let {
    children,
    className,
    ...props
  } = _ref21;
  const rest = getUnhandledProps(Card, props);
  const ElementType = detectElementType(Card, props);
  const classes = cx('card', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Card.handledProps = ["as", "className"];

const CardHeader = _ref22 => {
  let {
    children,
    className,
    ...props
  } = _ref22;
  const rest = getUnhandledProps(CardHeader, props);
  const ElementType = detectElementType(CardHeader, props);
  const classes = cx('card-header', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardHeader.handledProps = ["as", "className"];

const CardHeaderTitle = _ref23 => {
  let {
    children,
    className,
    ...props
  } = _ref23;
  const rest = getUnhandledProps(CardHeaderTitle, props);
  const ElementType = detectElementType(CardHeaderTitle, props);
  const classes = cx('card-header-title', className, {
    'is-centered': props.centered
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardHeaderTitle.handledProps = ["as", "centered", "className"];

const CardHeaderIcon = _ref24 => {
  let {
    children,
    className,
    ...props
  } = _ref24;
  const rest = getUnhandledProps(CardHeaderIcon, props);
  const ElementType = detectElementType(CardHeaderIcon, props);
  const classes = cx('card-header-icon', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardHeaderIcon.handledProps = ["as", "className"];

const CardImage = _ref25 => {
  let {
    children,
    className,
    ...props
  } = _ref25;
  const rest = getUnhandledProps(CardImage, props);
  const ElementType = detectElementType(CardImage, props);
  const classes = cx('card-image', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardImage.handledProps = ["as", "className"];

const CardContent = _ref26 => {
  let {
    children,
    className,
    ...props
  } = _ref26;
  const rest = getUnhandledProps(CardContent, props);
  const ElementType = detectElementType(CardContent, props);
  const classes = cx('card-content', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardContent.handledProps = ["as", "className"];

const CardFooter = _ref27 => {
  let {
    children,
    className,
    ...props
  } = _ref27;
  const rest = getUnhandledProps(CardFooter, props);
  const ElementType = detectElementType(CardFooter, props);
  const classes = cx('card-footer', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardFooter.handledProps = ["as", "className"];

const CardFooterItem = _ref28 => {
  let {
    children,
    className,
    ...props
  } = _ref28;
  const rest = getUnhandledProps(CardFooterItem, props);
  const ElementType = detectElementType(CardFooterItem, props);
  const classes = cx('card-footer-item', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

CardFooterItem.handledProps = ["as", "className"];

const Dropdown = _ref29 => {
  let {
    children,
    className,
    ...props
  } = _ref29;
  const {
    active,
    hoverable,
    right,
    up
  } = props;
  const rest = getUnhandledProps(Dropdown, props);
  const ElementType = detectElementType(Dropdown, props);
  const classes = cx('dropdown', className, {
    'is-active': active,
    'is-hoverable': hoverable,
    'is-right': right,
    'is-up': up
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Dropdown.handledProps = ["active", "as", "className", "hoverable", "right", "up"];

const DropdownTrigger = _ref30 => {
  let {
    children,
    className,
    ...props
  } = _ref30;
  const rest = getUnhandledProps(DropdownTrigger, props);
  const ElementType = detectElementType(DropdownTrigger, props);
  const classes = cx('dropdown-trigger', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

DropdownTrigger.handledProps = ["as", "className"];

const DropdownMenu = _ref31 => {
  let {
    children,
    className,
    ...props
  } = _ref31;
  const rest = getUnhandledProps(DropdownMenu, props);
  const ElementType = detectElementType(DropdownMenu, props);
  const classes = cx('dropdown-menu', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    role: "menu"
  }, rest, {
    className: classes
  }), children);
};

DropdownMenu.handledProps = ["as", "className"];

const DropdownContent = _ref32 => {
  let {
    children,
    className,
    ...props
  } = _ref32;
  const rest = getUnhandledProps(DropdownContent, props);
  const ElementType = detectElementType(DropdownContent, props);
  const classes = cx('dropdown-content', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

DropdownContent.handledProps = ["as", "className"];

const DropdownItem = _ref33 => {
  let {
    children,
    className,
    ...props
  } = _ref33;
  const rest = getUnhandledProps(DropdownItem, props);
  const ElementType = detectElementType(DropdownItem, props);
  const classes = cx('dropdown-item', className, {
    'is-active': props.active
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

DropdownItem.handledProps = ["active", "as", "className"];
DropdownItem.defaultProps = {
  as: 'a'
};

const DropdownDivider = _ref34 => {
  let {
    children,
    className,
    ...props
  } = _ref34;
  const rest = getUnhandledProps(DropdownDivider, props);
  const ElementType = detectElementType(DropdownDivider, props);
  const classes = cx('dropdown-divider', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

DropdownDivider.handledProps = ["as", "className"];
DropdownDivider.defaultProps = {
  as: 'hr'
};

const Menu = _ref35 => {
  let {
    children,
    className,
    ...props
  } = _ref35;
  const rest = getUnhandledProps(Menu, props);
  const ElementType = detectElementType(Menu, props);
  const classes = cx('menu', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Menu.handledProps = ["as", "className"];
Menu.defaultProps = {
  as: 'aside'
};

const MenuLabel = _ref36 => {
  let {
    children,
    className,
    ...props
  } = _ref36;
  const rest = getUnhandledProps(MenuLabel, props);
  const ElementType = detectElementType(MenuLabel, props);
  const classes = cx('menu-label', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

MenuLabel.handledProps = ["as", "className"];
MenuLabel.defaultProps = {
  as: 'p'
};

const MenuList = _ref37 => {
  let {
    children,
    className,
    ...props
  } = _ref37;
  const rest = getUnhandledProps(MenuList, props);
  const ElementType = detectElementType(MenuList, props);
  const classes = cx('menu-list', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

MenuList.handledProps = ["as", "className"];
MenuList.defaultProps = {
  as: 'ul'
};

const Message = _ref38 => {
  let {
    children,
    className,
    ...props
  } = _ref38;
  const {
    color,
    size
  } = props;
  const rest = getUnhandledProps(Message, props);
  const ElementType = detectElementType(Message, props);
  const classes = cx('message', className, {
    [`is-${color}`]: color,
    [`is-${size}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Message.handledProps = ["as", "className", "color", "size"];

const MessageHeader = _ref39 => {
  let {
    children,
    className,
    ...props
  } = _ref39;
  const rest = getUnhandledProps(MessageHeader, props);
  const ElementType = detectElementType(MessageHeader, props);
  const classes = cx('message-header', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

MessageHeader.handledProps = ["as", "className"];

const MessageBody = _ref40 => {
  let {
    children,
    className,
    ...props
  } = _ref40;
  const rest = getUnhandledProps(MessageBody, props);
  const ElementType = detectElementType(MessageBody, props);
  const classes = cx('message-body', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

MessageBody.handledProps = ["as", "className"];

const Modal = _ref41 => {
  let {
    children,
    className,
    ...props
  } = _ref41;
  const rest = getUnhandledProps(Modal, props);
  const ElementType = detectElementType(Modal, props);
  const classes = cx('modal', className, {
    'is-active': props.active
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Modal.handledProps = ["active", "as", "className"];

const ModalBackground = _ref42 => {
  let {
    children,
    className,
    ...props
  } = _ref42;
  const rest = getUnhandledProps(ModalBackground, props);
  const ElementType = detectElementType(ModalBackground, props);
  const classes = cx('modal-background', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalBackground.handledProps = ["as", "className"];

const ModalContent = _ref43 => {
  let {
    children,
    className,
    ...props
  } = _ref43;
  const rest = getUnhandledProps(ModalContent, props);
  const ElementType = detectElementType(ModalContent, props);
  const classes = cx('modal-content', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalContent.handledProps = ["as", "className"];

const ModalClose = _ref44 => {
  let {
    children,
    className,
    ...props
  } = _ref44;
  const rest = getUnhandledProps(ModalClose, props);
  const classes = cx('modal-close', className);
  return /*#__PURE__*/React.createElement(Button, _extends({
    "aria-label": "close"
  }, rest, {
    className: classes
  }), children);
};

ModalClose.handledProps = ["as", "className"];

const ModalCard = _ref45 => {
  let {
    children,
    className,
    ...props
  } = _ref45;
  const rest = getUnhandledProps(ModalCard, props);
  const ElementType = detectElementType(ModalCard, props);
  const classes = cx('modal-card', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalCard.handledProps = ["as", "className"];

const ModalCardHead = _ref46 => {
  let {
    children,
    className,
    ...props
  } = _ref46;
  const rest = getUnhandledProps(ModalCardHead, props);
  const ElementType = detectElementType(ModalCardHead, props);
  const classes = cx('modal-card-head', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalCardHead.handledProps = ["as", "className"];
ModalCardHead.defaultProps = {
  as: 'header'
};

const ModalCardTitle = _ref47 => {
  let {
    children,
    className,
    ...props
  } = _ref47;
  const rest = getUnhandledProps(ModalCardTitle, props);
  const ElementType = detectElementType(ModalCardTitle, props);
  const classes = cx('modal-card-title', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalCardTitle.handledProps = ["as", "className"];

const ModalCardBody = _ref48 => {
  let {
    children,
    className,
    ...props
  } = _ref48;
  const rest = getUnhandledProps(ModalCardBody, props);
  const ElementType = detectElementType(ModalCardBody, props);
  const classes = cx('modal-card-body', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalCardBody.handledProps = ["as", "className"];
ModalCardBody.defaultProps = {
  as: 'section'
};

const ModalCardFoot = _ref49 => {
  let {
    children,
    className,
    ...props
  } = _ref49;
  const rest = getUnhandledProps(ModalCardFoot, props);
  const ElementType = detectElementType(ModalCardFoot, props);
  const classes = cx('modal-card-foot', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

ModalCardFoot.handledProps = ["as", "className"];
ModalCardFoot.defaultProps = {
  as: 'footer'
};

const Navbar = _ref50 => {
  let {
    children,
    className,
    ...props
  } = _ref50;
  const {
    transparent,
    fixed,
    hasFixed,
    color,
    spaced
  } = props;
  const rest = getUnhandledProps(Navbar, props);
  const ElementType = detectElementType(Navbar, props);
  const classes = cx('navbar', className, {
    'is-transparent': transparent,
    [`is-fixed-${fixed}`]: fixed,
    [`has-navbar-fixed-${hasFixed}`]: hasFixed,
    [`is-${color}`]: color,
    'is-spaced': spaced
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    role: "navigation"
  }, rest, {
    className: classes
  }), children);
};

Navbar.handledProps = ["as", "className", "color", "fixed", "hasFixed", "spaced", "transparent"];

const NavbarBrand = _ref51 => {
  let {
    children,
    className,
    ...props
  } = _ref51;
  const rest = getUnhandledProps(NavbarBrand, props);
  const ElementType = detectElementType(NavbarBrand, props);
  const classes = cx('navbar-brand', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarBrand.handledProps = ["as", "className"];

const NavbarBurger = _ref52 => {
  let {
    children,
    className,
    ...props
  } = _ref52;
  const rest = getUnhandledProps(NavbarBurger, props);
  const ElementType = detectElementType(NavbarBurger, props);
  const classes = cx('navbar-burger', className, {
    'is-active': props.active
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    role: "button"
  }, rest, {
    className: classes
  }), children ? children : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  })));
};

NavbarBurger.handledProps = ["active", "as", "className"];
NavbarBurger.defaultProps = {
  as: 'a'
};

const NavbarMenu = _ref53 => {
  let {
    children,
    className,
    ...props
  } = _ref53;
  const rest = getUnhandledProps(NavbarMenu, props);
  const ElementType = detectElementType(NavbarMenu, props);
  const classes = cx('navbar-menu', className, {
    'is-active': props.active
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarMenu.handledProps = ["active", "as", "className"];

const NavbarStart = _ref54 => {
  let {
    children,
    className,
    ...props
  } = _ref54;
  const rest = getUnhandledProps(NavbarStart, props);
  const ElementType = detectElementType(NavbarStart, props);
  const classes = cx('navbar-start', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarStart.handledProps = ["as", "className"];

const NavbarEnd = _ref55 => {
  let {
    children,
    className,
    ...props
  } = _ref55;
  const rest = getUnhandledProps(NavbarEnd, props);
  const ElementType = detectElementType(NavbarEnd, props);
  const classes = cx('navbar-end', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarEnd.handledProps = ["as", "className"];

const NavbarItem = _ref56 => {
  let {
    children,
    className,
    ...props
  } = _ref56;
  const {
    dropdown,
    hoverable,
    active,
    expanded,
    tab,
    dropdownUp
  } = props;
  const rest = getUnhandledProps(NavbarItem, props);
  const ElementType = detectElementType(NavbarItem, props);
  const classes = cx('navbar-item', className, {
    'has-dropdown': dropdown,
    'is-hoverable': hoverable,
    'is-active': active,
    'is-expanded': expanded,
    'is-tab': tab,
    'has-dropdown-up': dropdownUp
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarItem.handledProps = ["active", "as", "className", "dropdown", "dropdownUp", "expanded", "hoverable", "tab"];
NavbarItem.defaultProps = {
  as: 'a'
};

const NavbarLink = _ref57 => {
  let {
    children,
    className,
    ...props
  } = _ref57;
  const rest = getUnhandledProps(NavbarLink, props);
  const ElementType = detectElementType(NavbarLink, props);
  const classes = cx('navbar-link', className, {
    'is-arrowless': props.arrowless
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarLink.handledProps = ["arrowless", "as", "className"];
NavbarLink.defaultProps = {
  as: 'a'
};

const NavbarDropdown = _ref58 => {
  let {
    children,
    className,
    ...props
  } = _ref58;
  const {
    active,
    alignment,
    boxed
  } = props;
  const rest = getUnhandledProps(NavbarDropdown, props);
  const ElementType = detectElementType(NavbarDropdown, props);
  const classes = cx('navbar-dropdown', className, {
    'is-active': active,
    [`is-${alignment}`]: alignment,
    'is-boxed': boxed
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarDropdown.handledProps = ["active", "alignment", "as", "boxed", "className"];

const NavbarDivider = _ref59 => {
  let {
    children,
    className,
    ...props
  } = _ref59;
  const rest = getUnhandledProps(NavbarDivider, props);
  const ElementType = detectElementType(NavbarDivider, props);
  const classes = cx('navbar-divider', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

NavbarDivider.handledProps = ["as", "className"];
NavbarDivider.defaultProps = {
  as: 'hr'
};

const Pagination = _ref60 => {
  let {
    children,
    className,
    ...props
  } = _ref60;
  const {
    alignment,
    rounded,
    size
  } = props;
  const rest = getUnhandledProps(Pagination, props);
  const ElementType = detectElementType(Pagination, props);
  const classes = cx('pagination', className, {
    [`is-${alignment}`]: alignment,
    'is-rounded': rounded,
    [`is-${size}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    role: "navigation"
  }, rest, {
    className: classes
  }), children);
};

Pagination.handledProps = ["alignment", "as", "className", "rounded", "size"];
Pagination.defaultProps = {
  as: 'nav'
};

const PaginationDirection = _ref61 => {
  let {
    children,
    className,
    ...props
  } = _ref61;
  const rest = getUnhandledProps(PaginationDirection, props);
  const ElementType = detectElementType(PaginationDirection, props);
  const classes = cx(`pagination-${props.direction}`, className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    role: "navigation"
  }, rest, {
    className: classes
  }), children);
};

PaginationDirection.handledProps = ["as", "className", "direction"];
PaginationDirection.defaultProps = {
  as: 'a'
};

const PaginationList = _ref62 => {
  let {
    children,
    className,
    ...props
  } = _ref62;
  const rest = getUnhandledProps(PaginationList, props);
  const classes = cx('pagination-list', className);
  return /*#__PURE__*/React.createElement("ul", _extends({}, rest, {
    className: classes
  }), children);
};

PaginationList.handledProps = ["as", "className"];

const PaginationLink = _ref63 => {
  let {
    children,
    className,
    ...props
  } = _ref63;
  const rest = getUnhandledProps(PaginationLink, props);
  const ElementType = detectElementType(PaginationLink, props);
  const classes = cx('pagination-link', className, {
    'is-current': props.current
  });
  return /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children));
};

PaginationLink.handledProps = ["as", "className", "current"];
PaginationLink.defaultProps = {
  as: 'a'
};

const PaginationEllipsis = _ref64 => {
  let {
    className,
    ...props
  } = _ref64;
  const rest = getUnhandledProps(PaginationEllipsis, props);
  const ElementType = detectElementType(PaginationEllipsis, props);
  const classes = cx('pagination-ellipsis', className);
  return /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), "\u2026"));
};

PaginationEllipsis.handledProps = ["as", "className"];
PaginationEllipsis.defaultProps = {
  as: 'span'
};

const Panel = _ref65 => {
  let {
    children,
    className,
    ...props
  } = _ref65;
  const rest = getUnhandledProps(Panel, props);
  const ElementType = detectElementType(Panel, props);
  const classes = cx('panel', className, {
    [`is-${props.color}`]: props.color
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Panel.handledProps = ["as", "className", "color"];
Panel.defaultProps = {
  as: 'nav'
};

const PanelHeading = _ref66 => {
  let {
    children,
    className,
    ...props
  } = _ref66;
  const rest = getUnhandledProps(PanelHeading, props);
  const ElementType = detectElementType(PanelHeading, props);
  const classes = cx('panel-heading', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

PanelHeading.handledProps = ["as", "className"];
PanelHeading.defaultProps = {
  as: 'p'
};

const PanelBlock = _ref67 => {
  let {
    children,
    className,
    ...props
  } = _ref67;
  const {
    active,
    icon
  } = props;
  const rest = getUnhandledProps(PanelBlock, props);
  const ElementType = detectElementType(PanelBlock, props);
  const classes = cx('panel-block', className, {
    'is-active': active
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), icon, children);
};

PanelBlock.handledProps = ["active", "as", "className", "icon"];

const PanelTabs = _ref68 => {
  let {
    children,
    className,
    ...props
  } = _ref68;
  const rest = getUnhandledProps(PanelTabs, props);
  const ElementType = detectElementType(PanelTabs, props);
  const classes = cx('panel-tabs', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

PanelTabs.handledProps = ["as", "className"];
PanelTabs.defaultProps = {
  as: 'p'
};

const Tabs = _ref69 => {
  let {
    children,
    className,
    ...props
  } = _ref69;
  const {
    alignment,
    size,
    boxed,
    toggle,
    toggleRounded,
    fullwidth
  } = props;
  const rest = getUnhandledProps(Tabs, props);
  const ElementType = detectElementType(Tabs, props);
  const classes = cx('tabs', className, {
    [`is-${alignment}`]: alignment,
    [`is-${size}`]: size,
    'is-boxed': boxed,
    'is-toggle': toggle,
    'is-toggle-rounded': toggleRounded,
    'is-fullwidth': fullwidth
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), /*#__PURE__*/React.createElement("ul", null, children));
};

Tabs.handledProps = ["alignment", "as", "boxed", "className", "fullwidth", "size", "toggle", "toggleRounded"];

const Tab = _ref70 => {
  let {
    children,
    className,
    ...props
  } = _ref70;
  const rest = getUnhandledProps(Tab, props);
  const classes = cx(className, {
    'is-active': props.active
  });
  return /*#__PURE__*/React.createElement("li", _extends({}, rest, {
    className: classes
  }), /*#__PURE__*/React.createElement("a", null, children));
};

Tab.handledProps = ["active", "as", "className"];

const Field = _ref71 => {
  let {
    children,
    className,
    ...props
  } = _ref71;
  const {
    label,
    labelSize,
    help,
    hasAddons,
    horizontal,
    grouped,
    groupedMultiline
  } = props;
  const rest = getUnhandledProps(Field, props);
  const ElementType = detectElementType(Field, props);
  const classes = cx('field', className, {
    'has-addons': hasAddons,
    'is-horizontal': horizontal,
    'is-grouped': grouped,
    'is-grouped-multiline': groupedMultiline
  });
  const fieldLabelClasses = cx('field-label', {
    [`is-${labelSize}`]: labelSize
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), label && (horizontal ? /*#__PURE__*/React.createElement("div", {
    className: fieldLabelClasses
  }, /*#__PURE__*/React.createElement("label", {
    className: "label"
  }, label)) : /*#__PURE__*/React.createElement("label", {
    className: "label"
  }, label)), horizontal ? /*#__PURE__*/React.createElement("div", {
    className: "field-body"
  }, /*#__PURE__*/React.createElement("div", {
    className: "field"
  }, children)) : children, help && /*#__PURE__*/React.createElement("p", {
    className: "help"
  }, help));
};

Field.handledProps = ["as", "className", "grouped", "groupedMultiline", "hasAddons", "help", "horizontal", "label", "labelSize"];

const Control = _ref72 => {
  let {
    children,
    className,
    ...props
  } = _ref72;
  const {
    loading,
    size,
    hasIcons
  } = props;
  const rest = getUnhandledProps(Control, props);
  const ElementType = detectElementType(Control, props);
  const classes = cx('control', className, {
    'is-loading': loading,
    [`is-${size}`]: size,
    'has-icons-left': hasIcons === 'left' || hasIcons === 'all',
    'has-icons-right': hasIcons === 'right' || hasIcons === 'all'
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Control.handledProps = ["as", "className", "hasIcons", "loading", "size"];

const Input = _ref73 => {
  let {
    children,
    className,
    ...props
  } = _ref73;
  const {
    color,
    size,
    rounded,
    hovered,
    focused
  } = props;
  const rest = getUnhandledProps(Input, props);
  const classes = cx('input', className, {
    [`is-${color}`]: color,
    [`is-${size}`]: size,
    'is-rounded': rounded,
    'is-hovered': hovered,
    'is-focused': focused,
    'is-static': props.static
  });
  return /*#__PURE__*/React.createElement("input", _extends({
    type: "text"
  }, rest, {
    className: classes
  }));
};

Input.handledProps = ["as", "className", "color", "focused", "hovered", "rounded", "size", "static"];

const Textarea = _ref74 => {
  let {
    children,
    className,
    ...props
  } = _ref74;
  const {
    color,
    size,
    hovered,
    focused,
    active,
    fixed
  } = props;
  const rest = getUnhandledProps(Textarea, props);
  const classes = cx('textarea', className, {
    [`is-${color}`]: color,
    [`is-${size}`]: size,
    'is-hovered': hovered,
    'is-focused': focused,
    'is-active': active,
    'has-fixed-size': fixed
  });
  return /*#__PURE__*/React.createElement("textarea", _extends({}, rest, {
    className: classes
  }), children);
};

Textarea.handledProps = ["active", "as", "className", "color", "fixed", "focused", "hovered", "size"];

const Select = _ref75 => {
  let {
    children,
    className,
    ...props
  } = _ref75;
  const {
    multiple,
    color,
    rounded,
    size,
    hovered,
    focused,
    loading,
    hasIcons,
    selectProps
  } = props;
  const ElementType = detectElementType(Select, props);
  const rest = getUnhandledProps(Select, props);
  const classes = cx('select', className, {
    'is-multiple': multiple,
    [`is-${color}`]: color,
    'is-rounded': rounded,
    [`is-${size}`]: size,
    'is-loading': loading,
    'has-icons-left': hasIcons === 'left' || hasIcons === 'all',
    'has-icons-right': hasIcons === 'right' || hasIcons === 'all'
  });
  const selectClasses = cx(selectProps === null || selectProps === void 0 ? void 0 : selectProps.className, {
    'is-hovered': hovered,
    'is-focused': focused
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), /*#__PURE__*/React.createElement("select", _extends({}, selectProps, {
    className: selectClasses
  }), children));
};

Select.handledProps = ["as", "className", "color", "focused", "hasIcons", "hovered", "loading", "multiple", "rounded", "selectProps", "size"];

const Checkbox = _ref76 => {
  let {
    children,
    className,
    ...props
  } = _ref76;
  const {
    labelProps
  } = props;
  const rest = getUnhandledProps(Checkbox, props);
  const labelClasses = cx('checkbox', labelProps === null || labelProps === void 0 ? void 0 : labelProps.className);
  return /*#__PURE__*/React.createElement("label", _extends({}, labelProps, {
    className: labelClasses
  }), /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    type: "checkbox"
  })), children);
};

Checkbox.handledProps = ["as", "className", "labelProps"];

const Radio = _ref77 => {
  let {
    children,
    className,
    ...props
  } = _ref77;
  const {
    labelProps
  } = props;
  const rest = getUnhandledProps(Radio, props);
  const labelClasses = cx('radio', labelProps === null || labelProps === void 0 ? void 0 : labelProps.className);
  return /*#__PURE__*/React.createElement("label", _extends({}, labelProps, {
    className: labelClasses
  }), /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    type: "radio"
  })), children);
};

Radio.handledProps = ["as", "className", "labelProps"];

const File = _ref78 => {
  let {
    children,
    className,
    ...props
  } = _ref78;
  const {
    icon,
    hasName,
    alignment,
    fullwidth,
    boxed,
    color,
    size
  } = props;
  const rest = getUnhandledProps(File, props);
  const ElementType = detectElementType(File, props);
  const classes = cx('file-input', className);
  const containerClasses = cx('file', {
    'has-name': hasName,
    [`is-${alignment}`]: alignment,
    'is-fullwidth': fullwidth,
    'is-boxed': boxed,
    [`is-${color}`]: color,
    [`is-${size}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, {
    className: containerClasses
  }, /*#__PURE__*/React.createElement("label", {
    className: "file-label"
  }, /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    type: "file",
    className: classes
  })), /*#__PURE__*/React.createElement("span", {
    className: "file-cta"
  }, /*#__PURE__*/React.createElement("span", {
    className: "file-icon"
  }, icon), /*#__PURE__*/React.createElement("span", {
    className: "file-label"
  }, children)), hasName && /*#__PURE__*/React.createElement("span", {
    className: "file-name"
  }, hasName)));
};

File.handledProps = ["alignment", "as", "boxed", "className", "color", "fullwidth", "hasName", "icon", "size"];

const Container = _ref79 => {
  let {
    children,
    className,
    ...props
  } = _ref79;
  const {
    breakpoint,
    max,
    fluid
  } = props;
  const rest = getUnhandledProps(Container, props);
  const ElementType = detectElementType(Container, props);
  const classes = cx('container', className, {
    [`is-${breakpoint}`]: breakpoint,
    [`is-max-${breakpoint}`]: max,
    [`is-fluid`]: fluid
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Container.handledProps = ["as", "breakpoint", "className", "fluid", "max"];

const Level = _ref80 => {
  let {
    children,
    className,
    ...props
  } = _ref80;
  const {
    left,
    right,
    item
  } = props;
  let levelClassName = 'level';

  if (left) {
    levelClassName = 'level-left';
  } else if (right) {
    levelClassName = 'level-right';
  } else if (item) {
    levelClassName = 'level-item';
  }

  const rest = getUnhandledProps(Level, props);
  const ElementType = detectElementType(Level, props);
  const classes = cx(levelClassName, className, {
    [`is-mobile`]: props.mobile
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Level.handledProps = ["as", "className", "item", "left", "mobile", "right"];

const Media = _ref81 => {
  let {
    children,
    className,
    ...props
  } = _ref81;
  const {
    left,
    right,
    content
  } = props;
  let mediaClassName = 'media';

  if (left) {
    mediaClassName = 'media-left';
  } else if (right) {
    mediaClassName = 'media-right';
  } else if (content) {
    mediaClassName = 'media-content';
  }

  const rest = getUnhandledProps(Media, props);
  const ElementType = detectElementType(Media, props);
  const classes = cx(mediaClassName, className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Media.handledProps = ["as", "className", "content", "left", "right"];

const Hero = _ref82 => {
  let {
    children,
    className,
    ...props
  } = _ref82;
  const {
    head,
    body,
    foot
  } = props;
  let heroClassName = 'hero';

  if (head) {
    heroClassName = 'hero-head';
  } else if (body) {
    heroClassName = 'hero-body';
  } else if (foot) {
    heroClassName = 'hero-foot';
  }

  const rest = getUnhandledProps(Hero, props);
  const ElementType = detectElementType(Hero, props);
  const classes = cx(heroClassName, className, {
    [`is-${props.color}`]: props.color,
    [`is-${props.size}`]: props.size,
    [`is-fullheight-with-navbar`]: props.fullheightWithNavbar
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Hero.handledProps = ["as", "body", "className", "color", "foot", "fullheightWithNavbar", "head", "size"];

const Section = _ref83 => {
  let {
    children,
    className,
    ...props
  } = _ref83;
  const rest = getUnhandledProps(Section, props);
  const ElementType = detectElementType(Section, props);
  const classes = cx('section', className, {
    [`is-${props.size}`]: props.size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Section.handledProps = ["as", "className", "size"];

const Footer = _ref84 => {
  let {
    children,
    className,
    ...props
  } = _ref84;
  const rest = getUnhandledProps(Footer, props);
  const ElementType = detectElementType(Footer, props);
  const classes = cx('footer', className);
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Footer.handledProps = ["as", "className"];
Footer.defaultProps = {
  as: 'footer'
};

const Tile = _ref85 => {
  let {
    children,
    className,
    ...props
  } = _ref85;
  const {
    ancestor,
    parent,
    child,
    vertical,
    size
  } = props;
  let tileClassName = 'tile';

  if (ancestor) {
    tileClassName += ' is-ancestor';
  } else if (parent) {
    tileClassName += ' is-parent';
  } else if (child) {
    tileClassName += ' is-child';
  }

  const rest = getUnhandledProps(Tile, props);
  const ElementType = detectElementType(Tile, props);
  const classes = cx(tileClassName, className, {
    'is-vertical': vertical,
    [`is-${properRange(size, [1, 12])}`]: size
  });
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
    className: classes
  }), children);
};

Tile.handledProps = ["ancestor", "as", "child", "className", "parent", "size", "vertical"];
const defaultProps$5 = {
  className: undefined,
  hasText: undefined,
  hasBackground: undefined
};

function withColor(Component) {
  return props => {
    const {
      hasText,
      hasBackground
    } = props;
    const rest = differenceObject(props, defaultProps$5);
    const classes = cx(props.className, {
      [`has-text-${hasText}`]: hasText,
      [`has-background-${hasBackground}`]: hasBackground
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withColor.handledProps = ["as", "className"];
const defaultProps$4 = {
  className: undefined,
  m: undefined,
  mt: undefined,
  mr: undefined,
  mb: undefined,
  ml: undefined,
  mx: undefined,
  my: undefined,
  p: undefined,
  pt: undefined,
  pr: undefined,
  pb: undefined,
  pl: undefined,
  px: undefined,
  py: undefined
};

function withSpacing(Component) {
  return props => {
    const {
      m,
      mt,
      mr,
      mb,
      ml,
      mx,
      my,
      p,
      pt,
      pr,
      pb,
      pl,
      px,
      py
    } = props;
    const rest = differenceObject(props, defaultProps$4);
    const classes = cx(props.className, {
      [`m-${properRange(m, [0, 6])}`]: m,
      [`mt-${properRange(mt, [0, 6])}`]: mt,
      [`mr-${properRange(mr, [0, 6])}`]: mr,
      [`mb-${properRange(mb, [0, 6])}`]: mb,
      [`ml-${properRange(ml, [0, 6])}`]: ml,
      [`mx-${properRange(mx, [0, 6])}`]: mx,
      [`my-${properRange(my, [0, 6])}`]: my,
      [`p-${properRange(p, [0, 6])}`]: p,
      [`pt-${properRange(pt, [0, 6])}`]: pt,
      [`pr-${properRange(pr, [0, 6])}`]: pr,
      [`pb-${properRange(pb, [0, 6])}`]: pb,
      [`pl-${properRange(pl, [0, 6])}`]: pl,
      [`px-${properRange(px, [0, 6])}`]: px,
      [`py-${properRange(py, [0, 6])}`]: py
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withSpacing.handledProps = ["as", "className"];
const defaultProps$3 = {
  isSize: undefined,
  viewport: undefined,
  textAlignment: undefined,
  responsive: undefined,
  textTransformation: undefined,
  textWeight: undefined,
  isFamily: undefined
};

function withTypography(Component) {
  return props => {
    const {
      isSize,
      viewport,
      textAlignment,
      responsive,
      textTransformation,
      textWeight,
      isFamily
    } = props;
    const rest = differenceObject(props, defaultProps$3);
    const classes = cx(props.className, {
      [`is-size-${properRange(isSize, [1, 7])}${viewport ? `-${viewport}` : ''}`]: isSize,
      [`has-text-${textAlignment}${responsive ? `-${responsive}` : ''}`]: textAlignment,
      [`is-${textTransformation}`]: textTransformation,
      [`has-text-weight-${textWeight}`]: textWeight,
      [`is-family-${isFamily}`]: isFamily
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withTypography.handledProps = ["as", "className"];
const defaultProps$2 = {
  className: undefined,
  isBlock: undefined,
  isFlex: undefined,
  isInline: undefined,
  isInlineBlock: undefined,
  isInlineFlex: undefined,
  isHidden: undefined,
  invisible: undefined,
  srOnly: undefined
};

function withResponsive(Component) {
  return props => {
    const {
      isBlock,
      isFlex,
      isInline,
      isInlineBlock,
      isInlineFlex,
      isHidden,
      invisible,
      srOnly
    } = props;
    const rest = differenceObject(props, defaultProps$2);
    const classes = cx(props.className, {
      [`is-block`]: typeof isBlock === 'boolean' && isBlock,
      [`is-block-${isBlock}`]: typeof isBlock === 'string' && isBlock,
      [`is-flex`]: typeof isFlex === 'boolean' && isFlex,
      [`is-flex-${isFlex}`]: typeof isFlex === 'string' && isFlex,
      [`is-inline`]: typeof isInline === 'boolean' && isInline,
      [`is-inline-${isInline}`]: typeof isInline === 'boolean' && isInline,
      [`is-inline-block`]: typeof isInlineBlock === 'boolean' && isInlineBlock,
      [`is-inline-block-${isInlineBlock}`]: typeof isInlineBlock === 'boolean' && isInlineBlock,
      [`is-inline-flex`]: typeof isInlineFlex === 'boolean' && isInlineFlex,
      [`is-inline-flex-${isInlineFlex}`]: typeof isInlineFlex === 'boolean' && isInlineFlex,
      [`is-hidden`]: typeof isHidden === 'boolean' && isHidden,
      [`is-hidden-${isHidden}`]: typeof isHidden === 'string' && isHidden,
      'is-invisible': invisible,
      'is-sr-only': srOnly
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withResponsive.handledProps = ["as", "className"];
const defaultProps$1 = {
  className: undefined,
  flexDirection: undefined,
  flexWrap: undefined,
  justifyContent: undefined,
  alignContent: undefined,
  alignItems: undefined,
  alignSelf: undefined,
  flexGrow: undefined,
  flexShrink: undefined
};

function withFlexbox(Component) {
  return props => {
    const {
      flexDirection,
      flexWrap,
      justifyContent,
      alignContent,
      alignItems,
      alignSelf,
      flexGrow,
      flexShrink
    } = props;
    const rest = differenceObject(props, defaultProps$1);
    const classes = cx(props.className, {
      [`is-flex-direction-${flexDirection}`]: flexDirection,
      [`is-flex-wrap`]: flexWrap,
      [`is-justify-content`]: justifyContent,
      [`is-align-content`]: alignContent,
      [`is-align-items`]: alignItems,
      [`is-align-self`]: alignSelf,
      [`is-flex-grow`]: flexGrow,
      [`is-flex-shrink`]: flexShrink
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withFlexbox.handledProps = ["as", "className"];
const defaultProps = {
  className: undefined,
  clearfix: undefined,
  pulled: undefined,
  overlay: undefined,
  clipped: undefined,
  radiusless: undefined,
  shadowless: undefined,
  bulmaUnselectable: undefined,
  clickable: undefined,
  relative: undefined
};

function withOther(Component) {
  return props => {
    const {
      clearfix,
      pulled,
      overlay,
      clipped,
      radiusless,
      shadowless,
      bulmaUnselectable,
      clickable,
      relative
    } = props;
    const rest = differenceObject(props, defaultProps);
    const classes = cx(props.className, {
      'is-clearfix': clearfix,
      [`is-pulled-${pulled}`]: pulled,
      'is-overlay': overlay,
      'is-clipped': clipped,
      'is-radiusless': radiusless,
      'is-shadowless': shadowless,
      'is-unselectable': bulmaUnselectable,
      'is-clickable': clickable,
      'is-relative': relative
    });
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: classes
    }, rest));
  };
}

withOther.handledProps = ["as", "className"];
export { Block, Box, Breadcrumb, Button, Buttons, Card, CardContent, CardFooter, CardFooterItem, CardHeader, CardHeaderIcon, CardHeaderTitle, CardImage, Checkbox, Column, Columns, Container, Content, Control, Delete, Dropdown, DropdownContent, DropdownDivider, DropdownItem, DropdownMenu, DropdownTrigger, Field, File, Footer, Hero, Icon, IconText, Image, Input, Level, Media, Menu, MenuLabel, MenuList, Message, MessageBody, MessageHeader, Modal, ModalBackground, ModalCard, ModalCardBody, ModalCardFoot, ModalCardHead, ModalCardTitle, ModalClose, ModalContent, Navbar, NavbarBrand, NavbarBurger, NavbarDivider, NavbarDropdown, NavbarEnd, NavbarItem, NavbarLink, NavbarMenu, NavbarStart, Notification, Pagination, PaginationDirection, PaginationEllipsis, PaginationLink, PaginationList, Panel, PanelBlock, PanelHeading, PanelTabs, Progress, Radio, Section, Select, Subtitle, Tab, Table, TableContainer, Tabs, Tag, Tags, Textarea, Tile, Title, withColor, withFlexbox, withOther, withResponsive, withSpacing, withTypography };